import { Alert, Button, Card, DatePicker, Empty, Space, Table, Tag } from 'antd';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Board = {
  unmarked: Array<{
    section_id: string;
    section_name?: string;
    grade_code?: string;
    period_code?: string;
    action_path?: string;
  }>;
  unmarked_periods: Array<{
    section_id: string;
    section_name?: string;
    grade_code?: string;
    period_code?: string;
    period_name?: string;
    subject_code?: string;
    subject_name?: string;
    action_path?: string;
  }>;
  corrections: Array<{
    id: string;
    student_id: string;
    admission_no?: string;
    first_name?: string;
    last_name?: string;
    requested_status: string;
    original_status?: string;
    section_name?: string;
    attendance_date?: string;
    period_code?: string;
  }>;
  unexplained: Array<{
    student_id: string;
    admission_no?: string;
    first_name?: string;
    last_name?: string;
    attendance_date: string;
    period_code?: string;
    section_name?: string;
  }>;
  repeated: Array<{
    student_id: string;
    admission_no?: string;
    first_name?: string;
    last_name?: string;
    absences: number;
  }>;
  threshold_configured: boolean;
};

function studentLabel(row: {
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  student_id: string;
}) {
  const name = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
  if (row.admission_no && name) return `${row.admission_no} — ${name}`;
  return name || row.admission_no || row.student_id;
}

export default function AttendanceExceptionsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const date = params.get('date') || dayjs().format('YYYY-MM-DD');
  const query = useQuery({
    queryKey: ['education-attendance-exceptions', date],
    enabled: Boolean(date),
    queryFn: () => api.get<Board>('/education/attendance/exceptions', { params: { attendance_date: date } }),
  });
  const board = query.data?.data;
  const unmarked = board?.unmarked?.length ?? 0;
  const unmarkedPeriods = board?.unmarked_periods?.length ?? 0;
  const corrections = board?.corrections?.length ?? 0;
  const unexplained = board?.unexplained?.length ?? 0;
  const repeated = board?.repeated?.length ?? 0;

  return (
    <EducationPageChrome
      title={t('attendanceExceptions')}
      description={t('attendanceOverviewIntro')}
      tags={
        <>
          <Tag>{date}</Tag>
          <Tag>
            {unmarked} {t('unmarked')}
          </Tag>
          <Tag>
            {unmarkedPeriods} {t('unmarkedPeriods', { defaultValue: 'Unmarked periods' })}
          </Tag>
          <Tag color="orange">
            {corrections} {t('pendingCorrections')}
          </Tag>
          <Tag color="red">
            {unexplained} {t('unexplainedAbsences')}
          </Tag>
          <Tag>
            {repeated} {t('repeatedAbsences')}
          </Tag>
        </>
      }
      extra={
        <Space wrap>
          <Button onClick={() => navigate('/education/attendance/settings')}>{t('attendanceSettings')}</Button>
          <Button onClick={() => navigate(`/education/attendance?${params.toString()}`)}>{t('backToAttendance')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <DatePicker
            value={dayjs(date)}
            format="DD-MMM-YYYY"
            onChange={(value) => {
              const next = new URLSearchParams(params);
              next.set('date', value ? value.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'));
              setParams(next);
            }}
          />
        </Card>
        <Card size="small" title={t('unmarked')}>
          <Table
            rowKey="section_id"
            loading={query.isLoading}
            dataSource={board?.unmarked || []}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            onRow={(row) => ({
              onClick: () => row.action_path && navigate(row.action_path),
              style: row.action_path ? { cursor: 'pointer' } : undefined,
            })}
            columns={[
              {
                title: t('section'),
                render: (_: unknown, row) =>
                  `${row.grade_code ? `${row.grade_code} ` : ''}${row.section_name || row.section_id}`,
              },
              { title: t('period'), dataIndex: 'period_code', width: 100 },
            ]}
          />
        </Card>
        <Card size="small" title={t('unmarkedPeriods', { defaultValue: 'Unmarked periods' })}>
          <Table
            rowKey={(row) => `${row.section_id}-${row.period_code}`}
            loading={query.isLoading}
            dataSource={board?.unmarked_periods || []}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            onRow={(row) => ({
              onClick: () => row.action_path && navigate(row.action_path),
              style: row.action_path ? { cursor: 'pointer' } : undefined,
            })}
            columns={[
              {
                title: t('section'),
                render: (_: unknown, row) =>
                  `${row.grade_code ? `${row.grade_code} ` : ''}${row.section_name || row.section_id}`,
              },
              {
                title: t('period'),
                render: (_: unknown, row) => `${row.period_code || ''}${row.period_name ? ` — ${row.period_name}` : ''}`,
              },
              {
                title: t('subject'),
                render: (_: unknown, row) =>
                  row.subject_code || row.subject_name
                    ? `${row.subject_code || ''} — ${row.subject_name || ''}`.replace(/^ — | — $/g, '')
                    : '—',
              },
            ]}
          />
        </Card>
        <Card size="small" title={t('pendingCorrections')}>
          <Table
            rowKey="id"
            loading={query.isLoading}
            dataSource={board?.corrections || []}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              { title: t('student'), render: (_: unknown, row) => studentLabel(row) },
              { title: t('section'), dataIndex: 'section_name', render: (v) => v || '—' },
              { title: t('period'), dataIndex: 'period_code', width: 100, render: (v) => v || '—' },
              {
                title: t('requestedStatus'),
                dataIndex: 'requested_status',
                render: (value: string) => <Tag>{value}</Tag>,
              },
            ]}
          />
        </Card>
        <Card size="small" title={t('unexplainedAbsences')}>
          <Table
            rowKey={(row) => `${row.student_id}-${row.attendance_date}-${row.period_code || 'daily'}`}
            loading={query.isLoading}
            dataSource={board?.unexplained || []}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              { title: t('student'), render: (_: unknown, row) => studentLabel(row) },
              { title: t('section'), dataIndex: 'section_name', render: (v) => v || '—' },
              { title: t('period'), dataIndex: 'period_code', width: 100, render: (v) => v || '—' },
              { title: t('attendanceDate'), dataIndex: 'attendance_date', width: 140 },
            ]}
          />
        </Card>
        <Card size="small" title={t('repeatedAbsences')}>
          {!board?.threshold_configured ? (
            <Alert type="info" showIcon message={t('thresholdNotConfigured')} style={{ marginBottom: 12 }} />
          ) : null}
          <Table
            rowKey="student_id"
            loading={query.isLoading}
            dataSource={board?.repeated || []}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
            columns={[
              { title: t('student'), render: (_: unknown, row) => studentLabel(row) },
              { title: t('repeatedAbsences'), dataIndex: 'absences', width: 120, align: 'right' },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
